/**
 * This component contains code to upload logs,format to upload logs should only be ".quantaenc"
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardHeader,
  IconButton,
  styled,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashedDivider from "common/ui/DashedDivider";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Typography from "@mui/material/Typography/Typography";
import { toast } from "react-hot-toast";
import { useUploadLog } from "services/machines.service";
import React from "react";
import { AxiosProgressEvent } from "axios";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectUploadLog } from "context/modal.selector";
import { setUploadLog } from "context/modal.context";
import UploadProgress from "types/UploadProgress.enum";
import { useQueryClient } from "@tanstack/react-query";
import LoadingButton from "common/ui/LoadingButton";
import "./UploadLog.scss";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

function UploadLog() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectUploadLog);

  const [progress, setProgress] = React.useState({
    status: UploadProgress.idle,
    value: 0
  });

  const uploadLog = useUploadLog(onSuccessfulUpload);

  function onSuccessfulUpload(_data, error) {
    queryClient.invalidateQueries({
      queryKey: ["machine-logs"],
      exact: false
    });

    if (!error) {
      setProgress({ status: UploadProgress.completed, value: 100 });
    } else {
      dismiss();
    }
  }

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";

    if (file.name?.match(/\.(quantaenc)$/i)) {
      setProgress({
        status: UploadProgress.uploading,
        value: 0
      });
      uploadLog.mutate({
        serialNumber: props.serialNumber,
        file,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setProgress({
            status: UploadProgress.uploading,
            value: percentCompleted
          });
        }
      });
    } else {
      toast.error(t("uploadLog.invalidFile"));
    }
  };

  function dismiss() {
    setProgress({ status: UploadProgress.idle, value: 0 });
    dispatch(
      setUploadLog({
        open: false,
        serialNumber: null
      })
    );
  }

  return (
    <Dialog open={props.open} maxWidth="sm" fullWidth>
      <DialogTitle
        component={CardHeader}
        avatar={<CloudUploadIcon color="primary" />}
        title="Upload Log"
        titleTypographyProps={{
          variant: "h6",
          fontWeight: "medium",
          color: "primary.main"
        }}
        action={
          <IconButton
            onClick={dismiss}
            disabled={progress.status === UploadProgress.uploading}
          >
            <CloseIcon />
          </IconButton>
        }
      />
      <DashedDivider style={{ width: "93%" }} />
      {progress.status === UploadProgress.idle ? (
        <DialogContent
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography
            variant="subtitle1"
            alignSelf="center"
            color="primary.main"
            fontWeight="medium"
          >
            {t("uploadLog.selectLog")}
          </Typography>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
          >
            <Typography variant="subtitle1">
              {t("uploadLog.browseFiles")}
            </Typography>
            <VisuallyHiddenInput type="file" onChange={handleUpload} />
          </Button>
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography
            variant="subtitle1"
            alignSelf="center"
            color="primary.main"
            align="center"
            fontWeight="medium"
          >
            {progress.status === UploadProgress.completed
              ? t("uploadLog.uploadComplete")
              : t("uploadLog.uploading")}
          </Typography>
          {progress.status === UploadProgress.uploading ? (
            <Stack
              direction="row"
              className="upload-log-modal__progress"
              spacing={1}
              px={2}
              py={1}
            >
              <CloudUploadIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                {t("uploadLog.progress", { count: progress.value })}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
      )}
      <DashedDivider style={{ width: "93%" }} />
      <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
        <LoadingButton
          variant="outlined"
          onClick={dismiss}
          loading={progress.status === UploadProgress.uploading}
        >
          {t("common.close")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadLog;
